import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type {
  MedicationStatement,
  AllergyIntolerance,
  Bundle,
  Resource,
} from '@medplum/fhirtypes';
import { OperationOutcomeError } from '@medplum/core';
import { v4 as uuidv4 } from 'uuid';
import type {
  MedicationsData,
  MedicationLocal,
  SupplementLocal,
  AllergyLocal,
  AllergySeverity,
  AllergyCategory,
} from '../types/medications';
import {
  MEDICATION_CATEGORY,
  MEDICATION_STATUS,
  ALLERGY_CLINICAL_STATUS,
  ALLERGY_VERIFICATION_STATUS,
} from '../types/medications';
import { onboardingService, type MedicationsReviewed } from '../services/onboardingService';
import { parseFlexibleDate, getNoteText } from '../utils/fhirHelpers';

// Extension URL to mark supplements vs medications
const SUPPLEMENT_EXTENSION_URL = 'http://precisionlongevity.io/fhir/StructureDefinition/is-supplement';

// Helper: Check if MedicationStatement is a supplement
export function isSupplement(med: MedicationStatement): boolean {
  return med.extension?.some(ext => ext.url === SUPPLEMENT_EXTENSION_URL && ext.valueBoolean === true) ?? false;
}

// Extract MedicationsData from FHIR resources
export function extractMedicationsData(
  medicationStatements: MedicationStatement[],
  allergyIntolerances: AllergyIntolerance[]
): MedicationsData {
  const medications: MedicationLocal[] = [];
  const supplements: SupplementLocal[] = [];

  for (const med of medicationStatements) {
    const noteText = getNoteText(med);

    // Parse dosage
    const dosageText = med.dosage?.[0]?.text || '';
    const dosageParts = dosageText.split(' - ');
    const dosage = dosageParts[0] || '';
    const frequency = dosageParts[1] || '';

    if (isSupplement(med)) {
      supplements.push({
        localId: uuidv4(),
        fhirId: med.id,
        supplementName: med.medicationCodeableConcept?.text || '',
        dosage: dosage || undefined,
        frequency: frequency || undefined,
        comments: noteText || undefined,
      });
    } else {
      medications.push({
        localId: uuidv4(),
        fhirId: med.id,
        medicationName: med.medicationCodeableConcept?.text || '',
        dosage: dosage || undefined,
        frequency: frequency || undefined,
        startDate: parseFlexibleDate(med.effectiveDateTime as string | undefined),
        comments: noteText || undefined,
      });
    }
  }

  const allergies: AllergyLocal[] = allergyIntolerances.map((allergy) => {
    const noteText = getNoteText(allergy);
    const reaction = allergy.reaction?.[0]?.manifestation?.[0]?.text || '';
    const severity = allergy.reaction?.[0]?.severity as AllergySeverity | undefined;
    const category = allergy.category?.[0] as AllergyCategory | undefined;

    return {
      localId: uuidv4(),
      fhirId: allergy.id,
      allergyName: allergy.code?.text || '',
      category,
      reaction: reaction || undefined,
      severity,
      comments: noteText || undefined,
    };
  });

  return {
    medications,
    supplements,
    allergies,
  };
}

// Build FHIR MedicationStatement from local data
export function buildMedicationResource(
  local: MedicationLocal | SupplementLocal,
  patientId: string,
  type: 'medication' | 'supplement'
): MedicationStatement {
  const isMed = type === 'medication';
  const name = isMed
    ? (local as MedicationLocal).medicationName
    : (local as SupplementLocal).supplementName;

  const medicationStatement: MedicationStatement = {
    resourceType: 'MedicationStatement',
    ...(local.fhirId && { id: local.fhirId }),
    subject: { reference: `Patient/${patientId}` },
    status: MEDICATION_STATUS.active,
    medicationCodeableConcept: {
      text: name,
    },
    category: {
      coding: [MEDICATION_CATEGORY.medication],
    },
  };

  // Mark supplements with extension
  if (!isMed) {
    medicationStatement.extension = [
      {
        url: SUPPLEMENT_EXTENSION_URL,
        valueBoolean: true,
      },
    ];
  }

  // Add dosage (combine dosage and frequency)
  const dosage = local.dosage || '';
  const frequency = local.frequency || '';
  if (dosage || frequency) {
    const dosageText = [dosage, frequency].filter(Boolean).join(' - ');
    medicationStatement.dosage = [{ text: dosageText }];
  }

  // Add start date for medications
  if (isMed) {
    const medLocal = local as MedicationLocal;
    if (medLocal.startDate?.value) {
      medicationStatement.effectiveDateTime = medLocal.startDate.value;
    }
  }

  // Add comments as note
  if (local.comments) {
    medicationStatement.note = [{ text: local.comments }];
  }

  return medicationStatement;
}

// Build FHIR AllergyIntolerance from local data
export function buildAllergyResource(
  local: AllergyLocal,
  patientId: string
): AllergyIntolerance {
  const allergyIntolerance: AllergyIntolerance = {
    resourceType: 'AllergyIntolerance',
    ...(local.fhirId && { id: local.fhirId }),
    patient: { reference: `Patient/${patientId}` },
    clinicalStatus: {
      coding: [ALLERGY_CLINICAL_STATUS.active],
    },
    verificationStatus: {
      coding: [ALLERGY_VERIFICATION_STATUS.unconfirmed],
    },
    code: {
      text: local.allergyName,
    },
  };

  // Add category if provided
  if (local.category) {
    allergyIntolerance.category = [local.category];
  }

  // Add reaction if provided (manifestation is required in FHIR)
  if (local.reaction || local.severity) {
    allergyIntolerance.reaction = [
      {
        manifestation: [{ text: local.reaction || 'Unknown reaction' }],
        ...(local.severity && { severity: local.severity }),
      },
    ];
  }

  // Add comments as note
  if (local.comments) {
    allergyIntolerance.note = [{ text: local.comments }];
  }

  return allergyIntolerance;
}

export function useMedications() {
  const medplum = useMedplum();
  const baseProfile = useMedplumProfile() as Resource | undefined;

  const [medicationStatements, setMedicationStatements] = useState<MedicationStatement[]>([]);
  const [allergyIntolerances, setAllergyIntolerances] = useState<AllergyIntolerance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewedFlags, setReviewedFlags] = useState<MedicationsReviewed>(
    onboardingService.getMedicationsReviewed()
  );

  const patientId = baseProfile?.id;
  const resourceType = (baseProfile as { resourceType?: string })?.resourceType;

  // Load existing data from Medplum
  const loadData = useCallback(async () => {
    if (!patientId || resourceType !== 'Patient') {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch medication statements - don't fail if none found
      try {
        const medsResult = await medplum.search('MedicationStatement', {
          subject: `Patient/${patientId}`,
          _sort: '-_lastUpdated',
        });
        const medsBundle = medsResult as Bundle<MedicationStatement>;
        setMedicationStatements(
          medsBundle.entry?.map((e) => e.resource as MedicationStatement) || []
        );
      } catch {
        // No medications found - that's okay
        setMedicationStatements([]);
      }

      // Fetch allergies - don't fail if none found
      try {
        const allergiesResult = await medplum.search('AllergyIntolerance', {
          patient: `Patient/${patientId}`,
          _sort: '-_lastUpdated',
        });
        const allergiesBundle = allergiesResult as Bundle<AllergyIntolerance>;
        setAllergyIntolerances(
          allergiesBundle.entry?.map((e) => e.resource as AllergyIntolerance) || []
        );
      } catch {
        // No allergies found - that's okay
        setAllergyIntolerances([]);
      }
    } catch (err) {
      console.error('Failed to load medications data:', err);
      if (err instanceof OperationOutcomeError) {
        console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
      }
      setError('Unable to load medications data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [medplum, patientId, resourceType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Extract to local format
  const medicationsData = useMemo(
    () => extractMedicationsData(medicationStatements, allergyIntolerances),
    [medicationStatements, allergyIntolerances]
  );

  // Data status for each section
  const hasMedications = medicationsData.medications.length > 0;
  const hasSupplements = medicationsData.supplements.length > 0;
  const hasAllergies = medicationsData.allergies.length > 0;

  // Section is complete if: has data OR marked as "no content"
  const isMedicationsComplete = hasMedications || reviewedFlags.noMedications;
  const isSupplementsComplete = hasSupplements || reviewedFlags.noSupplements;
  const isAllergiesComplete = hasAllergies || reviewedFlags.noAllergies;

  // Overall completion: all sections complete
  const isComplete = isMedicationsComplete && isSupplementsComplete && isAllergiesComplete;

  // Has any data at all
  const hasAnyData = hasMedications || hasSupplements || hasAllergies;

  // Update reviewed flags
  const setReviewedFlag = useCallback((flag: keyof MedicationsReviewed, value: boolean) => {
    const updated = onboardingService.setMedicationsReviewed({ [flag]: value });
    setReviewedFlags(updated);
  }, []);

  // Save all changes
  const saveAll = useCallback(
    async (data: MedicationsData): Promise<boolean> => {
      if (!patientId) {
        setError('Patient data not loaded');
        return false;
      }

      // Validate required fields
      for (const med of data.medications) {
        if (!med.isDeleted && !med.medicationName.trim()) {
          setError('Please fill in the medication name for all entries.');
          return false;
        }
      }
      for (const supp of data.supplements) {
        if (!supp.isDeleted && !supp.supplementName.trim()) {
          setError('Please fill in the supplement name for all entries.');
          return false;
        }
      }
      for (const allergy of data.allergies) {
        if (!allergy.isDeleted && !allergy.allergyName.trim()) {
          setError('Please fill in the allergy name for all entries.');
          return false;
        }
      }

      try {
        setIsSaving(true);
        setError(null);

        const operations: Promise<unknown>[] = [];

        // Save medications
        for (const med of data.medications) {
          if (med.isDeleted && med.fhirId) {
            await medplum.deleteResource('MedicationStatement', med.fhirId);
          } else if (!med.isDeleted && med.medicationName) {
            const resource = buildMedicationResource(med, patientId, 'medication');
            if (med.fhirId) {
              operations.push(medplum.updateResource(resource));
            } else {
              operations.push(medplum.createResource(resource));
            }
          }
        }

        // Save supplements
        for (const supp of data.supplements) {
          if (supp.isDeleted && supp.fhirId) {
            await medplum.deleteResource('MedicationStatement', supp.fhirId);
          } else if (!supp.isDeleted && supp.supplementName) {
            const resource = buildMedicationResource(supp, patientId, 'supplement');
            if (supp.fhirId) {
              operations.push(medplum.updateResource(resource));
            } else {
              operations.push(medplum.createResource(resource));
            }
          }
        }

        // Save allergies
        for (const allergy of data.allergies) {
          if (allergy.isDeleted && allergy.fhirId) {
            await medplum.deleteResource('AllergyIntolerance', allergy.fhirId);
          } else if (!allergy.isDeleted && allergy.allergyName) {
            const resource = buildAllergyResource(allergy, patientId);
            if (allergy.fhirId) {
              operations.push(medplum.updateResource(resource));
            } else {
              operations.push(medplum.createResource(resource));
            }
          }
        }

        await Promise.all(operations);

        // Reload data to get updated IDs
        await loadData();
        return true;
      } catch (err) {
        console.error('Failed to save medications data:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to save. Please try again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [medplum, patientId, loadData]
  );

  return {
    medicationsData,
    isLoading,
    isSaving,
    error,
    isComplete,
    hasAnyData,
    // Per-section status
    hasMedications,
    hasSupplements,
    hasAllergies,
    isMedicationsComplete,
    isSupplementsComplete,
    isAllergiesComplete,
    // Reviewed flags
    reviewedFlags,
    setReviewedFlag,
    // Actions
    saveAll,
    refresh: loadData,
  };
}
